import type { ReportingProduct, ReportingSearchPerformanceDay, ReportingSeoData } from "./reporting"

export const SEO_PAGE_GROUPS = [
  { key: "home", label: "Home" },
  { key: "tool", label: "Tools" },
  { key: "managed-leaf", label: "Managed leaves" },
  { key: "hub", label: "Hubs" },
  { key: "other", label: "Other / unmatched" },
] as const

function isCatalogVariant(
  entity: string,
  hosts: string[],
  domain: string | null | undefined,
  catalogUrls: Set<string>,
): boolean {
  try {
    const parsed = new URL(entity)
    const candidates = [
      parsed.pathname,
      parsed.pathname.replace(/\/$/, "") || "/",
      `${parsed.pathname.replace(/\/$/, "")}/`,
    ]
    return hosts.includes(parsed.hostname) && candidates.some((path) => catalogUrls.has(`https://${domain}${path}`))
  } catch {
    return false
  }
}

/** Exact URLs only. A query, slash, protocol or host variant is never silently merged. */
export function deriveSeoPageGroups(
  data: ReportingSeoData,
  product: ReportingProduct,
  dates: string[],
  pageDays: ReportingSearchPerformanceDay[],
) {
  const inventory = data.seoInventory?.products.find((entry) => entry.productKey === product.key)
  const identities = new Set(
    pageDays.map((day) => JSON.stringify([day.sourceKey, day.sourceProperty, day.queryVersion])),
  )
  const inventoryAvailable = inventory?.status === "available"
  const ready = inventoryAvailable && identities.size <= 1
  const exportComplete = dates.length > 0 && pageDays.length === dates.length && identities.size === 1
  const catalog = new Map(inventory?.pages.map((page) => [page.url, page]) ?? [])
  const catalogUrls = new Set(catalog.keys())
  const exported = new Map<string, { clicks: number; impressions: number }>()
  if (ready)
    for (const day of pageDays)
      for (const row of day.rows) {
        const previous = exported.get(row.entity) ?? { clicks: 0, impressions: 0 }
        exported.set(row.entity, {
          clicks: previous.clicks + row.clicks,
          impressions: previous.impressions + row.impressions,
        })
      }
  const groups = SEO_PAGE_GROUPS.map((group) => ({ ...group, clicks: 0, impressions: 0, urls: 0 }))
  let variantUrls = 0
  for (const [entity, totals] of exported) {
    const page = catalog.get(entity)
    const group = groups.find((candidate) => candidate.key === (page?.kind ?? "other"))
    group.clicks += totals.clicks
    group.impressions += totals.impressions
    group.urls += 1
    if (!page && isCatalogVariant(entity, product.hosts, inventory?.domain, catalogUrls)) variantUrls += 1
  }
  const totalClicks = groups.reduce((sum, group) => sum + group.clicks, 0)
  const totalImpressions = groups.reduce((sum, group) => sum + group.impressions, 0)
  const managed = inventory?.pages.filter((page) => page.kind === "managed-leaf") ?? []
  const published = managed.filter((page) => page.state === "published")
  const observedPublished = published.filter((page) => exported.has(page.url)).length
  return {
    status: ready ? "available" : "missing",
    reason: identities.size > 1 ? "SOURCE_AMBIGUOUS" : (inventory?.reason ?? (inventory ? null : "NOT_BOUND")),
    collectedAt: data.seoInventory?.collectedAt ?? null,
    sourceKey: inventory?.sourceKey ?? null,
    exportDays: pageDays.length,
    expectedDays: dates.length,
    exportComplete,
    groups:
      ready && pageDays.length
        ? groups.map((group) => ({ ...group, share: totalClicks > 0 ? group.clicks / totalClicks : null }))
        : [],
    totalClicks: ready && pageDays.length ? totalClicks : null,
    totalImpressions: ready && pageDays.length ? totalImpressions : null,
    variantUrls: ready ? variantUrls : null,
    managedPages: inventoryAvailable ? managed.length : null,
    publishedPages: inventoryAvailable ? published.length : null,
    observedPublished: ready && pageDays.length ? observedPublished : null,
    publishedWithoutReturnedRow: ready && exportComplete ? published.length - observedPublished : null,
    collectionIds: ready ? pageDays.map((day) => day.collectionId) : [],
  }
}
